import React, { useEffect } from "react";
import CommonDialog from "app/components/common/CommonDialog";
import {
  Box,
  DialogActions,
  DialogContent,
  Grid,
  Stack,
  IconButton,
  Chip,
  Switch,
  FormControlLabel,
  Divider,
  TextField,
  MenuItem,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import {
  CustomTextField,
  CustomSelectBox,
} from "assets/styles/globalStyledComponent";
import { LoadingButton } from "@mui/lab";
import { useMutation } from "react-query";
import { toast } from "react-toastify";
import { Add, Delete, Code, List } from "@mui/icons-material";
import TextFieldLabel from "app/components/common/TextFieldLabel";
import { upsertTenantInfo } from "app/services/tenant/orgTenant.service";

const AddTenantForm = ({
  open = false,
  edit = false,
  onClose,
  refetch,
  editData,
}) => {
  const [state, setState] = React.useState({
    allowedDomains: [""],
    isJsonMode: false,
    jsonText: "",
    showCustomLoginUrl: false,
  });

  const defaultValues = {
    tenantName: "",
    tenantid: "",
    schoolDocId: "",
    environment: "",
    erpLoginUrl: "",
    customLoginUrl: "",
    schoolWebUrl: "",
  };

  const { control, handleSubmit, reset } = useForm({
    defaultValues: defaultValues,
  });

  useEffect(() => {
    // Only populate form when dialog is open
    if (open) {
      if (edit && editData) {
        const domains =
          editData?.allowedDomains?.length > 0 ? editData.allowedDomains : [""];
        const isCustomUrl =
          editData?.erpLoginUrl &&
          !["", "school.ekalsutra.com", "beta.ekalsutra.com"].includes(
            editData.erpLoginUrl
          );

        setState((prev) => ({
          ...prev,
          allowedDomains: domains,
          jsonText: JSON.stringify(domains, null, 2),
          showCustomLoginUrl: isCustomUrl,
          isJsonMode: false,
        }));
        reset({
          tenantName: editData?.tenantName ?? "",
          tenantid: editData?.tenantid ?? "",
          schoolDocId: editData?.schoolDocId ?? "",
          environment: editData?.environment ?? "",
          erpLoginUrl: isCustomUrl ? "other" : editData?.erpLoginUrl ?? "",
          customLoginUrl: isCustomUrl ? editData?.erpLoginUrl : "",
          schoolWebUrl: editData?.schoolWebUrl ?? "",
        });
      } else {
        // Reset for add mode
        setState((prev) => ({
          ...prev,
          allowedDomains: [""],
          jsonText: '[""]',
          isJsonMode: false,
          showCustomLoginUrl: false,
        }));
        reset({
          tenantName: "",
          tenantid: "",
          schoolDocId: "",
          environment: "",
          erpLoginUrl: "",
          customLoginUrl: "",
          schoolWebUrl: "",
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, edit, editData]);

  const handleResetState = () => {
    setState((prev) => ({
      ...prev,
      allowedDomains: [""],
      jsonText: '[""]',
      isJsonMode: false,
      showCustomLoginUrl: false,
    }));
    reset(defaultValues);
  };

  const handleClose = () => {
    onClose();
    handleResetState();
  };

  const handleAddDomain = () => {
    const newDomains = [...state.allowedDomains, ""];
    setState((prev) => ({
      ...prev,
      allowedDomains: newDomains,
      jsonText: JSON.stringify(newDomains, null, 2),
    }));
  };

  const handleRemoveDomain = (index) => {
    const newDomains = state.allowedDomains.filter((_, i) => i !== index);
    setState((prev) => ({
      ...prev,
      allowedDomains: newDomains,
      jsonText: JSON.stringify(newDomains, null, 2),
    }));
  };

  const handleDomainChange = (index, value) => {
    const newDomains = state.allowedDomains.map((domain, i) =>
      i === index ? value : domain
    );

    setState((prev) => ({
      ...prev,
      allowedDomains: newDomains,
      jsonText: JSON.stringify(newDomains, null, 2),
    }));
  };

  const handleJsonModeToggle = () => {
    setState((prev) => ({
      ...prev,
      isJsonMode: !prev.isJsonMode,
    }));
  };

  const handleJsonTextChange = (value) => {
    setState((prev) => ({
      ...prev,
      jsonText: value,
    }));

    // Try to parse and update allowedDomains
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        setState((prev) => ({
          ...prev,
          allowedDomains: parsed,
        }));
      }
    } catch (error) {
      // Invalid JSON, keep current state
    }
  };

  const removeDuplicates = () => {
    const uniqueDomains = [
      ...new Set(state.allowedDomains.filter((domain) => domain.trim() !== "")),
    ];
    if (uniqueDomains.length === 0) uniqueDomains.push("");

    setState((prev) => ({
      ...prev,
      allowedDomains: uniqueDomains,
      jsonText: JSON.stringify(uniqueDomains, null, 2),
    }));

    toast.success("Duplicate domains removed");
  };

  const syncFromJson = () => {
    try {
      const parsed = JSON.parse(state.jsonText);
      if (Array.isArray(parsed)) {
        const uniqueDomains = [
          ...new Set(
            parsed.filter(
              (domain) => typeof domain === "string" && domain.trim() !== ""
            )
          ),
        ];
        if (uniqueDomains.length === 0) uniqueDomains.push("");

        setState((prev) => ({
          ...prev,
          allowedDomains: uniqueDomains,
          jsonText: JSON.stringify(uniqueDomains, null, 2),
        }));
        toast.success("JSON synchronized successfully");
      } else {
        toast.error("JSON must be an array of strings");
      }
    } catch (error) {
      toast.error("Invalid JSON format");
    }
  };

  const { mutate: upsertTenantInfoMutate, isLoading: upsertTenantInfoLoading } =
    useMutation(upsertTenantInfo, {
      onSuccess: ({ data }) => {
        refetch();
        toast.success(data?.message || "Tenant saved successfully");
        handleClose();
      },
      onError: ({ response: { data } }) => {
        toast.error(data?.message || "Failed to save tenant");
      },
    });

  const onSubmit = async (data) => {
    // Filter out empty domains
    const validDomains = state.allowedDomains.filter(
      (domain) => domain.trim() !== ""
    );

    if (validDomains.length === 0) {
      toast.error("Please add at least one allowed domain");
      return;
    }

    // Determine the actual login URL
    const finalLoginUrl =
      data?.erpLoginUrl === "other" ? data?.customLoginUrl : data?.erpLoginUrl;

    const payload = {
      tenantName: data?.tenantName,
      tenantid: data?.tenantid,
      schoolDocId: data?.schoolDocId,
      environment: data?.environment,
      erpLoginUrl: finalLoginUrl,
      schoolWebUrl: data?.schoolWebUrl,
      allowedDomains: validDomains,
    };

    // If editing, include the ID
    if (edit && editData?._id) {
      payload._id = editData._id;
    }

    upsertTenantInfoMutate(payload);
  };

  return (
    <>
      <CommonDialog
        open={open}
        onClose={handleClose}
        title={edit ? "Edit Tenant Information" : "Add Tenant Information"}
        minWidth="800px"
        maxWidth="md"
      >
        <DialogContent sx={{ p: 3 }}>
          <Stack spacing={2}>
            {/* Basic Information Section */}
            <Box
              sx={{
                border: "1px solid #E0E7F1",
                backgroundColor: "#F8FAFC",
                p: 2.5,
                borderRadius: "12px",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
              }}
            >
              <TextFieldLabel
                title="Basic Information"
                styleProps={{
                  fontSize: "18px",
                  fontWeight: "700",
                  color: "#1E40AF",
                  mb: 1,
                }}
              />

              <Grid container spacing={2}>
                {/* Tenant Name  */}
                <Grid item xs={12} md={6}>
                  <TextFieldLabel
                    title="Tenant Name"
                    required
                    styleProps={{ fontWeight: "600", mb: 1 }}
                  />
                  <Controller
                    name="tenantName"
                    control={control}
                    rules={{
                      required: "Tenant name is required",
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <CustomTextField
                        {...field}
                        size="small"
                        variant="outlined"
                        placeholder="Enter tenant name"
                        error={!!error}
                        helperText={error?.message}
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                {/* Tenant ID  */}
                <Grid item xs={12} md={6}>
                  <TextFieldLabel
                    title="Tenant ID"
                    required
                    styleProps={{ fontWeight: "600", mb: 1 }}
                  />
                  <Controller
                    name="tenantid"
                    control={control}
                    rules={{
                      required: "Tenant ID is required",
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <CustomTextField
                        {...field}
                        size="small"
                        variant="outlined"
                        placeholder="Enter tenant ID"
                        error={!!error}
                        helperText={error?.message}
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                {/* SchoolDocId */}
                <Grid item xs={6}>
                  <TextFieldLabel
                    title="School Document ID"
                    required
                    styleProps={{ fontWeight: "600", mb: 1 }}
                  />
                  <Controller
                    name="schoolDocId"
                    control={control}
                    rules={{
                      required: "School Document ID is required",
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <CustomTextField
                        {...field}
                        size="small"
                        variant="outlined"
                        placeholder="Enter school document ID"
                        error={!!error}
                        helperText={error?.message}
                        fullWidth
                      />
                    )}
                  />
                </Grid>

                {/* Environment  */}
                <Grid item xs={6}>
                  <TextFieldLabel
                    title="Environment"
                    required
                    styleProps={{ fontWeight: "600", mb: 1 }}
                  />
                  <Controller
                    name="environment"
                    control={control}
                    rules={{
                      required: "Environment is required",
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <CustomSelectBox
                          {...field}
                          size="small"
                          displayEmpty
                          fullWidth
                          error={!!error}
                        >
                          <MenuItem value="" disabled>
                            Select Environment
                          </MenuItem>
                          <MenuItem value="prod">Prod</MenuItem>
                          <MenuItem value="beta">Beta</MenuItem>
                          <MenuItem value="dev">Dev</MenuItem>
                        </CustomSelectBox>
                        {error && (
                          <Box
                            sx={{
                              color: "error.main",
                              fontSize: "0.75rem",
                              mt: 0.5,
                              ml: 1.5,
                            }}
                          >
                            {error.message}
                          </Box>
                        )}
                      </>
                    )}
                  />
                </Grid>

                {/* ERP Login URL  */}
                <Grid item xs={6}>
                  <TextFieldLabel
                    title="ERP Login URL"
                    required
                    styleProps={{ fontWeight: "600", mb: 1 }}
                  />
                  <Controller
                    name="erpLoginUrl"
                    control={control}
                    rules={{
                      required: "ERP Login URL is required",
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <CustomSelectBox
                          {...field}
                          size="small"
                          displayEmpty
                          fullWidth
                          error={!!error}
                          onChange={(e) => {
                            field.onChange(e);
                            setState((prev) => ({
                              ...prev,
                              showCustomLoginUrl: e.target.value === "other",
                            }));
                          }}
                        >
                          <MenuItem value="" disabled>
                            Select ERP Login URL
                          </MenuItem>
                          <MenuItem value="school.ekalsutra.com">
                            school.ekalsutra.com
                          </MenuItem>
                          <MenuItem value="beta.ekalsutra.com">
                            beta.ekalsutra.com
                          </MenuItem>
                          <MenuItem value="other">Other</MenuItem>
                        </CustomSelectBox>
                        {error && (
                          <Box
                            sx={{
                              color: "error.main",
                              fontSize: "0.75rem",
                              mt: 0.5,
                              ml: 1.5,
                            }}
                          >
                            {error.message}
                          </Box>
                        )}
                      </>
                    )}
                  />
                </Grid>
                 {/* School Web URL */}
                <Grid item xs={6}>
                  <TextFieldLabel
                    title="School Web URL"
                    styleProps={{ fontWeight: "600", mb: 1 }}
                  />
                  <Controller
                    name="schoolWebUrl"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <CustomTextField
                        {...field}
                        size="small"
                        variant="outlined"
                        placeholder="Enter school web URL"
                        error={!!error}
                        helperText={error?.message}
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                {/* Custom Erp Login URL (shown when 'other' is selected) */}
                {state.showCustomLoginUrl && (
                  <Grid item xs={12}>
                    <TextFieldLabel
                      title="Custom Erp Login URL"
                      required
                      styleProps={{ fontWeight: "600", mb: 1 }}
                    />
                    <Controller
                      name="customLoginUrl"
                      control={control}
                      rules={{
                        required: state.showCustomLoginUrl
                          ? "Custom Erp Login URL is required"
                          : false,
                      }}
                      render={({ field, fieldState: { error } }) => (
                        <CustomTextField
                          {...field}
                          size="small"
                          variant="outlined"
                          placeholder="Enter custom Erp Login URL"
                          error={!!error}
                          helperText={error?.message}
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>

            {/* Allowed Domains Section */}
            <Box
              sx={{
                border: "1px solid #E0E7F1",
                backgroundColor: "#FEF3F2",
                p: 2.5,
                borderRadius: "12px",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <TextFieldLabel
                  title="Allowed Domains"
                  styleProps={{
                    fontSize: "18px",
                    fontWeight: "700",
                    color: "#DC2626",
                  }}
                />
                <Stack direction="row" spacing={1}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={state.isJsonMode}
                        onChange={handleJsonModeToggle}
                        size="small"
                      />
                    }
                    label={
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        {state.isJsonMode ? (
                          <Code fontSize="small" />
                        ) : (
                          <List fontSize="small" />
                        )}
                        <span style={{ fontSize: "12px" }}>
                          {state.isJsonMode ? "JSON Mode" : "Form Mode"}
                        </span>
                      </Stack>
                    }
                    sx={{ mr: 1 }}
                  />
                  <LoadingButton
                    size="small"
                    variant="outlined"
                    onClick={removeDuplicates}
                    sx={{ textTransform: "none", fontSize: "11px" }}
                  >
                    Remove Duplicates
                  </LoadingButton>
                </Stack>
              </Stack>

              {state.isJsonMode ? (
                /* JSON Editor Mode */
                <Stack spacing={2}>
                  <Box>
                    <TextFieldLabel
                      title="JSON Array Editor"
                      subtitle="Enter domains as a JSON array"
                      styleProps={{
                        fontSize: "14px",
                        fontWeight: "600",
                        mb: 1,
                      }}
                    />
                    <TextField
                      multiline
                      rows={3}
                      fullWidth
                      variant="outlined"
                      value={state.jsonText}
                      onChange={(e) => handleJsonTextChange(e.target.value)}
                      placeholder='["domain1.com", "domain2.com", "domain3.com"]'
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "#FFFFFF",
                          fontFamily: "'Courier New', Consolas, monospace",
                          fontSize: "14px",
                          lineHeight: 1.5,
                          padding: "12px",
                          "& textarea": {
                            resize: "vertical",
                            minHeight: "50px !important",
                          },
                        },
                        "& .MuiOutlinedInput-input": {
                          padding: "0 !important",
                        },
                        "& .MuiInputBase-inputMultiline": {
                          padding: "0 !important",
                        },
                      }}
                    />
                  </Box>
                  <Stack direction="row" spacing={1}>
                    <LoadingButton
                      size="small"
                      variant="contained"
                      onClick={syncFromJson}
                      sx={{ textTransform: "none" }}
                    >
                      Sync from JSON
                    </LoadingButton>
                    <LoadingButton
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        setState((prev) => ({
                          ...prev,
                          jsonText: JSON.stringify(
                            state.allowedDomains,
                            null,
                            2
                          ),
                        }))
                      }
                      sx={{ textTransform: "none" }}
                    >
                      Format JSON
                    </LoadingButton>
                  </Stack>
                </Stack>
              ) : (
                /* Form Mode */
                <Stack spacing={2}>
                  {state.allowedDomains.map((domain, index) => (
                    <Box
                      key={index}
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <CustomTextField
                        size="small"
                        variant="outlined"
                        placeholder="Enter domain (e.g., school.example.com)"
                        value={domain}
                        onChange={(e) =>
                          handleDomainChange(index, e.target.value)
                        }
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "#FFFFFF",
                          },
                        }}
                      />
                      {state.allowedDomains.length > 1 && (
                        <IconButton
                          onClick={() => handleRemoveDomain(index)}
                          color="error"
                          size="small"
                        >
                          <Delete />
                        </IconButton>
                      )}
                    </Box>
                  ))}

                  {/* Add Domain Button */}
                  <Box
                    sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}
                  >
                    <LoadingButton
                      size="small"
                      color="primary"
                      onClick={handleAddDomain}
                      startIcon={<Add />}
                      sx={{
                        textTransform: "none",
                        px: 2,
                        fontWeight: 400,
                        color: "#3B82F6",
                        "&:hover": {
                          border: "1px solid",
                          borderColor: "#21519eff",
                          backgroundColor: "#EBF8FF",
                        },
                      }}
                    >
                      Add Domain
                    </LoadingButton>
                  </Box>
                </Stack>
              )}

              <Divider sx={{ my: 2 }} />

              {state.allowedDomains.filter((domain) => domain.trim() !== "")
                .length > 0 && (
                <Box>
                  <TextFieldLabel
                    title="Preview:"
                    styleProps={{ fontSize: "14px", fontWeight: "600", mb: 1 }}
                  />
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {[
                      ...new Set(
                        state.allowedDomains.filter(
                          (domain) => domain.trim() !== ""
                        )
                      ),
                    ].map((domain, index) => (
                      <Chip
                        key={index}
                        label={domain}
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                    ))}
                  </Stack>
                  {new Set(state.allowedDomains.filter((d) => d.trim() !== ""))
                    .size !==
                    state.allowedDomains.filter((d) => d.trim() !== "")
                      .length && (
                    <TextFieldLabel
                      title="⚠️ Duplicates detected - click 'Remove Duplicates' to clean"
                      styleProps={{ fontSize: "12px", color: "#FF6B35", mt: 1 }}
                    />
                  )}
                </Box>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions
          sx={{
            borderTop: "1px solid #E5E7EB",
            backgroundColor: "#F9FAFB",
          }}
        >
          <LoadingButton
            variant="outlined"
            color="secondary"
            onClick={handleClose}
          >
            Cancel
          </LoadingButton>
          <LoadingButton
            variant="contained"
            color="secondary"
            loading={upsertTenantInfoLoading}
            onClick={handleSubmit(onSubmit)}
          >
            {edit ? "Update Tenant" : "Add Tenant"}
          </LoadingButton>
        </DialogActions>
      </CommonDialog>
    </>
  );
};

export default AddTenantForm;
